import { createFileRoute, Link } from "@tanstack/react-router";
import { CITIES, CITY_SLUGS } from "@/data/cities";
import { OCCASIONS, OCCASION_SLUGS } from "@/data/occasions";
import { EQUIPMENT, EQUIPMENT_SLUGS } from "@/data/equipment";
import { VENUES, VENUE_SLUGS } from "@/data/venues";
import { POSTS, MERGED_POSTS } from "@/data/posts";
import { INDEXABLE_SERVICE_CITIES } from "@/data/city-service-content";
import { metaDesc } from "@/lib/seo-description";

export const Route = createFileRoute("/plan-du-site")({
  head: () => ({
    meta: [
      { title: "Plan du site | DJ Val" },
      { name: "description", content: metaDesc("Toutes les pages du site DJ Val : prestations DJ, villes d'intervention en Provence, lieux de réception, matériel en location, guides et actualités.") },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Plan du site — DJ Val" },
      { property: "og:description", content: metaDesc("Toutes les pages du site : prestations, villes, lieux, matériel, actualités.") },
      { property: "og:url", content: "https://dj-val.fr/plan-du-site" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/plan-du-site" }],
  }),
  component: SitemapPage,
});

const FOOTER_CITIES = CITY_SLUGS.filter((s) => s !== "evangelique");

const CITIES_BY_REGION = FOOTER_CITIES.reduce<Record<string, string[]>>((acc, s) => {
  const r = CITIES[s].region;
  (acc[r] ||= []).push(s);
  return acc;
}, {});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="text-[11px] uppercase tracking-[0.4em] text-[var(--gold)] mb-4 border-b border-[var(--gold-line)] pb-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

const linkCls = "block py-1 text-sm text-foreground/90 hover:text-[var(--gold)] transition-colors";

function SitemapPage() {
  return (
    <main className="bg-background text-foreground">
      <header className="border-b border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 text-center">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[var(--gold)] mb-3">Navigation complète</p>
          <h1 className="font-serif-display text-4xl md:text-5xl italic">Plan du site</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Toutes les pages, prestations et zones d'intervention de DJ Val en un coup d'œil.
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <Section title="Principales pages">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            <Link to="/" className={linkCls}>Accueil</Link>
            <Link to="/a-propos" className={linkCls}>À propos</Link>
            <Link to="/tarifs" className={linkCls}>Tarifs</Link>
            <Link to="/realisations" className={linkCls}>Réalisations</Link>
            <Link to="/galerie" className={linkCls}>Galerie</Link>
            <Link to="/avis-clients" className={linkCls}>Avis clients</Link>
            <Link to="/contactez-nous" className={linkCls}>Contact</Link>
            <Link to="/contactez-nous" className={linkCls}>Devis gratuit</Link>
            <Link to="/calculateur" className={linkCls}>Calculateur de prix</Link>
            <Link to="/actualites" className={linkCls}>Actualités</Link>
            <Link to="/guide-mariage-vaucluse" className={linkCls}>Guide mariage Vaucluse</Link>
          </div>
        </Section>

        <Section title="Prestations">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            <Link to="/dj-mariage" className={linkCls}>DJ Mariage</Link>
            <Link to="/dj-mariage/pack-sono-lumiere" className={linkCls}>Pack sono & lumière mariage</Link>
            <Link to="/dj-mariage/$ville" params={{ ville: "evangelique" }} className={linkCls}>DJ Mariage évangélique</Link>
            <Link to="/dj-vaucluse" className={linkCls}>DJ Vaucluse</Link>
            <Link to="/ciel-etoile-mariage" className={linkCls}>Ciel étoilé mariage</Link>
            <Link to="/soirees-evenements" className={linkCls}>Soirées & événements</Link>
            <Link to="/soirees-thematiques" className={linkCls}>Soirées thématiques</Link>
            <Link to="/sono-lumiere" className={linkCls}>Sono & Lumière</Link>
            <Link to="/location-materiel" className={linkCls}>Location de matériel</Link>
            <Link to="/location-sono-mariage" className={linkCls}>Location sono mariage</Link>
            <Link to="/location-gender-reveal" className={linkCls}>Gender Reveal</Link>
          </div>
        </Section>

        <Section title="Types d'événements">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            {OCCASION_SLUGS.map((s) => (
              <Link key={s} to="/soirees-evenements/$type" params={{ type: s }} className={linkCls}>
                {OCCASIONS[s].name}
              </Link>
            ))}
          </div>
        </Section>

        <Section title="Zones d'intervention">
          {Object.entries(CITIES_BY_REGION).map(([region, slugs]) => (
            <div key={region} className="mb-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-foreground/85 mb-2">{region}</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6">
                {slugs.map((s) => (
                  <div key={s} className="py-1">
                    <Link to="/dj-mariage/$ville" params={{ ville: s }} className="text-sm text-foreground/90 hover:text-[var(--gold)] transition-colors">
                      DJ Mariage {CITIES[s].name}
                    </Link>
                    {(INDEXABLE_SERVICE_CITIES as readonly string[]).includes(s) && (
                      <div className="flex gap-3 mt-0.5 text-[11px] text-muted-foreground">
                        <Link to="/dj-entreprise/$ville" params={{ ville: s }} className="hover:text-[var(--gold)] transition-colors">Entreprise</Link>
                        <Link to="/dj-anniversaire/$ville" params={{ ville: s }} className="hover:text-[var(--gold)] transition-colors">Anniversaire</Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        <Section title="Lieux de réception">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            <Link to="/lieux" className={linkCls + " font-medium text-[var(--gold)]"}>Tous les lieux</Link>
            {VENUE_SLUGS.map((s) => (
              <Link key={s} to="/lieux/$slug" params={{ slug: s }} className={linkCls}>
                {VENUES[s].name}
              </Link>
            ))}
          </div>
        </Section>

        <Section title="Matériel disponible">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            {EQUIPMENT.map((e) => (
              <Link key={e.slug} to="/location-materiel/$slug" params={{ slug: e.slug }} className={linkCls}>
                {e.name}
              </Link>
            ))}
          </div>
        </Section>

        <Section title="Articles & guides">
          <div className="grid sm:grid-cols-2 gap-x-8">
            {POSTS.filter((p) => !p.noindex && !MERGED_POSTS[p.slug]).map((p) => (
              <Link key={p.slug} to="/actualites/$slug" params={{ slug: p.slug }} className={linkCls}>
                {p.title}
              </Link>
            ))}
          </div>
        </Section>

        <Section title="Informations légales">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8">
            <Link to="/mentions-legales" className={linkCls}>Mentions légales</Link>
            <Link to="/politique-confidentialite" className={linkCls}>Politique de confidentialité</Link>
            <Link to="/conditions-de-location" className={linkCls}>CGV location</Link>
          </div>
        </Section>
      </div>
    </main>
  );
}
